import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, CaretDown, Check, HouseLine, InstagramLogo, TrendUp, UserFocus, UsersThree, VideoCamera, X } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ADDONS } from "@/lib/addons";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { PACKAGES } from "@/lib/packages";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/09d18691-9bcf-4619-a866-359b60aa1b93/images/01e660114c63508bee552c6d4f5c92a8d6225e8cd826c1796c2459af97d36c5e.jpeg";
const COACH_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_mobile-flip-academy/artifacts/duir6mzo_9C3A5277-Edit.jpg";
const BOOK_IMG = "https://m.media-amazon.com/images/I/71qgvJNz-nL.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const FAQS = [
  { q: "Do I need real estate experience to start?", a: "No. The core program assumes zero experience. Module one begins with the foundations of mobile home investing and works up to scaling your business." },
  { q: "How is mobile home flipping different from house flipping?", a: "Lower purchase prices, faster transactions, less competition, and creative financing options that banks rarely touch. The margins are real — if you know how to evaluate the deal, which is exactly what we teach." },
  { q: "What's included in the 1-on-1 calls?", a: "Both Full Flip Formula and Inner Circle include 2× 30-minute private calls with Ciara — deal reviews, strategy, and answers to your questions. You book them from your dashboard once you're enrolled." },
  { q: "How long do I have access to the modules?", a: "30 days from the day you enroll. Your video access unlocks on purchase and locks automatically after 30 days — this keeps the curriculum current and fair to all students. Renewing before your time runs out adds another 30 days on top of what's left. Workbook PDFs you print are yours to keep." },
  { q: "Where can I get the book 'Flipping Mobiles From A to Z'?", a: "The book is available on Amazon. It's the perfect companion to the course — many students read it first, then join a package to go deeper." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="bg-background" data-testid="landing-page">
      <div className="grain-overlay" />

      {/* Nav */}
      <header data-testid="main-nav" className="sticky top-0 z-40 border-b border-border bg-[#F9F9F7]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <HouseLine size={24} weight="duotone" className="text-primary" />
            <span className="font-heading text-2xl tracking-tight">TTPI Academy</span>
          </Link>
          <nav className="hidden items-center gap-8 md:flex">
            <a href="#packages" data-testid="nav-link-packages" className="text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground">Packages</a>
            <a href="#coach" data-testid="nav-link-coach" className="text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground">Coach</a>
            <a href="#book" data-testid="nav-link-book" className="text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground">Book</a>
            <a href="#faq" data-testid="nav-link-faq" className="text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground">FAQ</a>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-menu-dropdown" className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground">
                Menu <CaretDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-none border-border bg-white" data-testid="nav-menu-content">
                {ADDONS.map((a, i) => (
                  <DropdownMenuItem key={a.title} asChild className="cursor-pointer">
                    <Link to="/services" data-testid={`nav-menu-item-${i}`} className="text-xs uppercase tracking-[0.15em]">
                      {a.title}{a.price ? ` — ${a.price}` : ""}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="flex items-center gap-1 p-2 text-xs uppercase tracking-[0.2em] text-foreground md:hidden" aria-label="Toggle menu">
              {mobileOpen ? <>Close <X size={14} /></> : <>Menu <CaretDown size={12} /></>}
            </button>
            {user ? (
              <Link to="/dashboard" data-testid="nav-dashboard-link" className="border border-foreground bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-background transition-colors duration-200 hover:bg-primary hover:border-primary hover:text-primary-foreground">
                My Dashboard
              </Link>
            ) : (
              <>
                <Link to="/auth" data-testid="nav-login-link" className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-foreground transition-colors duration-200 hover:text-primary">
                  Login
                </Link>
                <a href="#packages" data-testid="nav-get-started" className="hidden border border-foreground bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-background transition-colors duration-200 hover:bg-primary hover:border-primary hover:text-primary-foreground md:inline-block">
                  Get Started
                </a>
              </>
            )}
          </div>
        </div>
        {mobileOpen && (
          <nav className="border-t border-border bg-[#F9F9F7] px-6 py-5 md:hidden" data-testid="nav-mobile-panel">
            <div className="flex flex-col gap-4">
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-dashboard" className="text-xs uppercase tracking-[0.2em] text-foreground">My Dashboard</Link>
              ) : (
                <a href="#packages" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-get-started" className="text-xs uppercase tracking-[0.2em] text-foreground">Get Started</a>
              )}
              <a href="#packages" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-packages" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Packages</a>
              <a href="#coach" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-coach" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Coach</a>
              <a href="#book" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-book" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Book</a>
              <a href="#faq" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-faq" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">FAQ</a>
              <div className="border-t border-border pt-4">
                <p className="mb-3 text-xs uppercase tracking-[0.2em] text-primary">Services &amp; Add-ons</p>
                <div className="flex flex-col gap-3">
                  {ADDONS.map((a, i) => (
                    <Link key={a.title} to="/services" onClick={() => setMobileOpen(false)} data-testid={`nav-mobile-addon-${i}`} className="text-xs uppercase tracking-[0.15em] text-foreground">
                      {a.title}{a.price ? ` — ${a.price}` : ""}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        )}
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-16 lg:px-10 lg:pt-24">
        <div className="grid grid-cols-1 items-end gap-12 lg:grid-cols-12">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="overline-label mb-6" data-testid="hero-overline">Mobile Home Flipping Academy</p>
            <h1 data-testid="hero-heading" className="font-heading text-5xl font-light leading-[1.05] sm:text-6xl lg:text-7xl">
              Flip mobile homes.<br />
              <span className="italic text-primary">Build lasting wealth.</span>
            </h1>
            <p data-testid="hero-subheading" className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground">
              The most overlooked asset class in real estate — taught step by step by Ciara Toliver, author of <em>Flipping Mobiles From A to Z</em>. Recorded modules, live coaching, and real-deal walkthroughs.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#packages" data-testid="hero-cta-enroll" className="group inline-flex items-center gap-2 border border-foreground bg-foreground px-8 py-4 text-xs uppercase tracking-[0.15em] text-background transition-colors duration-300 hover:bg-primary hover:border-primary hover:text-primary-foreground">
                View Packages <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a href="#coach" data-testid="hero-cta-coach" className="inline-flex items-center gap-2 border border-foreground/20 px-8 py-4 text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-foreground">
                Meet Ciara
              </a>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="lg:col-span-7">
            <div className="border border-border p-2">
              <div className="relative overflow-hidden">
                <img src={HERO_IMG} alt="Modern mobile home in nature" data-testid="hero-image" className="aspect-[16/10] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <p className="absolute bottom-4 left-4 text-xs uppercase tracking-[0.2em] text-white/90">Real assets. Real margins.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-y border-border bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-border px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-10">
          {[
            { icon: VideoCamera, title: "Recorded Modules", text: "Prerecorded video lessons and printable workbooks you can watch on your schedule during your 30-day access." },
            { icon: UserFocus, title: "1-on-1 Coaching", text: "Private sessions with Ciara to review your deals and sharpen your strategy." },
            { icon: UsersThree, title: "Templates & Community", text: "Deal documents, workbooks, and a network of investors walking the same path." },
          ].map((p, i) => (
            <motion.div key={p.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="flex gap-5 px-4 py-12" data-testid={`pillar-${i}`}>
              <p.icon size={32} weight="duotone" className="shrink-0 text-primary" />
              <div>
                <h3 className="font-heading text-2xl">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Coach */}
      <section id="coach" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="border border-border p-2">
              <img src={COACH_IMG} alt="Coach Ciara Toliver" data-testid="coach-image" className="aspect-[4/5] w-full object-cover" />
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="flex flex-col justify-center lg:col-span-7 lg:pl-8">
            <p className="overline-label mb-6">Your Coach</p>
            <h2 data-testid="coach-heading" className="font-heading text-4xl font-light leading-tight sm:text-5xl">
              Ciara Toliver turned overlooked homes into a <span className="italic text-primary">wealth engine</span>.
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
              After years of flipping mobile homes — and writing the book on it — Ciara built TTPI Academy to hand you the exact playbook: how to find underpriced homes, fund deals creatively, renovate on a budget, and exit fast for real profit.
            </p>
            <div className="mt-10 grid max-w-xl grid-cols-3 divide-x divide-border border border-border">
              {[
                { value: "A→Z", label: "Complete system" },
                { value: "6", label: "Video modules" },
                { value: "3", label: "Coaching paths" },
              ].map((s, i) => (
                <div key={i} className="px-6 py-6" data-testid={`stat-${i}`}>
                  <p className="font-heading text-3xl text-primary">{s.value}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
            <a
              href="https://www.instagram.com/toptailoredpropinv"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="coach-instagram-link"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-primary transition-colors duration-200 hover:text-foreground"
            >
              <InstagramLogo size={18} /> Follow @toptailoredpropinv
            </a>
          </motion.div>
        </div>
      </section>

      {/* Book */}
      <section id="book" className="scroll-mt-24 border-y border-border bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32">
          <motion.div {...fadeUp} className="flex flex-col justify-center lg:col-span-7 lg:pr-8">
            <p className="overline-label mb-6 flex items-center gap-2"><BookOpen size={16} /> The Book — Beginner's Edition</p>
            <h2 data-testid="book-heading" className="font-heading text-4xl font-light leading-tight sm:text-5xl">
              How to Flip Mobile Homes <span className="italic text-primary">From A to Z</span>
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
              Ciara's Amazon book is the written companion to the academy — the full framework from finding your first deal to cashing the check. Read it, then let the course walk you through doing it.
            </p>
            <div className="mt-10">
              <a
                data-testid="book-amazon-link"
                href="https://www.amazon.com/How-flip-mobile-homes-Beginners/dp/B0DR6GNQ47"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 border border-foreground px-8 py-4 text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:bg-foreground hover:text-background"
              >
                Get the Book on Amazon <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5">
            <div className="border border-border p-2">
              <img src={BOOK_IMG} alt="How to Flip Mobile Homes From A to Z — book cover" data-testid="book-image" className="aspect-[4/3] w-full bg-[#F1F0EB] object-contain p-10" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 lg:px-10 lg:py-32">
        <motion.div {...fadeUp} className="mb-16 max-w-2xl">
          <p className="overline-label mb-6">Enrollment Packages</p>
          <h2 data-testid="packages-heading" className="font-heading text-4xl font-light leading-tight sm:text-5xl">
            Choose how deep you want to go.
          </h2>
          <p data-testid="access-policy-note" className="mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Every package includes <strong className="text-foreground">30 days of access</strong> to your video modules from the day you enroll. Access locks automatically after 30 days. Printable workbook PDFs are yours to keep.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 gap-px border border-border bg-border md:grid-cols-3">
          {PACKAGES.map((pkg, i) => {
            const isElite = pkg.id === "inner_circle";
            return (
              <motion.div
                key={pkg.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`package-card-${pkg.id}`}
                className={`flex flex-col p-10 lg:p-12 ${isElite ? "bg-[#141413] text-[#F4F4F0]" : "bg-white"}`}
              >
                <p className={`overline-label ${isElite ? "text-[#A3A39C]" : ""}`}>{pkg.tagline}</p>
                <h3 className="mt-4 font-heading text-4xl">{pkg.name}</h3>
                <p className={`mt-6 font-heading text-5xl font-light ${isElite ? "text-[#C49A6C]" : "text-primary"}`}>
                  ${pkg.price.toLocaleString()}
                </p>
                <p className={`mt-2 text-xs uppercase tracking-[0.15em] ${isElite ? "text-[#A3A39C]" : "text-muted-foreground"}`}>{pkg.term}</p>
                <p className={`mt-6 text-sm leading-relaxed ${isElite ? "text-[#A3A39C]" : "text-muted-foreground"}`}>{pkg.description}</p>
                <ul className="mt-8 flex-1 space-y-3">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check size={16} weight="bold" className={`mt-0.5 shrink-0 ${isElite ? "text-[#C49A6C]" : "text-primary"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`package-${pkg.id}-button`}
                  onClick={() => startCheckout(pkg.id, user, navigate)}
                  className={`mt-10 inline-flex items-center justify-center gap-2 border px-8 py-4 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    isElite
                      ? "border-[#C49A6C] bg-[#C49A6C] text-[#141413] hover:bg-transparent hover:text-[#C49A6C]"
                      : "border-foreground bg-foreground text-background hover:bg-primary hover:border-primary hover:text-primary-foreground"
                  }`}
                >
                  Enroll in {pkg.name} <ArrowRight size={16} />
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-y border-border bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <motion.div {...fadeUp} className="mb-16">
            <p className="overline-label mb-6">Student Stories</p>
            <h2 className="font-heading text-4xl font-light sm:text-5xl">Deals are getting done.</h2>
          </motion.div>
          <div className="grid grid-cols-1 gap-px border border-border bg-border md:grid-cols-3">
            {[
              { quote: "I closed my first flip 11 weeks after finishing the Starter modules. The deal analysis checklist alone paid for the course.", name: "Marcus T.", detail: "Starter → first flip" },
              { quote: "The 1-on-1 sessions saved me from a bad park deal that would have cost me thousands. Ciara tells you the truth.", name: "Denise W.", detail: "Pro student" },
              { quote: "Walking properties with Ciara in the field changed everything. You can't learn negotiation from a video alone — Elite was worth every penny.", name: "Ray & Carla J.", detail: "Elite students" },
            ].map((t, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="bg-white p-10" data-testid={`testimonial-${i}`}>
                <TrendUp size={24} weight="duotone" className="text-primary" />
                <p className="mt-6 font-heading text-xl italic leading-relaxed">"{t.quote}"</p>
                <p className="mt-6 text-xs uppercase tracking-[0.15em]">{t.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl scroll-mt-24 px-6 py-24 lg:py-32">
        <motion.div {...fadeUp} className="mb-12">
          <p className="overline-label mb-6">Questions</p>
          <h2 className="font-heading text-4xl font-light sm:text-5xl">Before you enroll.</h2>
        </motion.div>
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-heading text-xl">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Quote CTA */}
      <section className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-20 md:flex-row md:items-center lg:px-10" data-testid="quote-cta-section">
          <div>
            <p className="overline-label mb-4">Handyman Services</p>
            <h2 className="max-w-xl font-heading text-3xl font-light leading-tight sm:text-4xl">
              Need work done on a mobile or manufactured home? <span className="italic text-primary">We're on it.</span>
            </h2>
          </div>
          <span
            data-testid="quote-coming-soon"
            className="inline-flex shrink-0 items-center border border-foreground/30 px-8 py-4 text-xs uppercase tracking-[0.15em] text-muted-foreground"
          >
            Coming Soon
          </span>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#141413] text-[#F4F4F0]">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-16 md:flex-row md:items-end md:justify-between lg:px-10">
          <div>
            <div className="flex items-center gap-2">
              <HouseLine size={24} weight="duotone" className="text-[#C49A6C]" />
              <span className="font-heading text-2xl">TTPI Academy</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#A3A39C]">
              Mobile home flipping education by Ciara Toliver — author of <em>Flipping Mobiles From A to Z</em>.
            </p>
          </div>
          <div className="flex gap-10 text-xs uppercase tracking-[0.15em] text-[#A3A39C]">
            <a href="#packages" className="transition-colors duration-200 hover:text-[#F4F4F0]">Packages</a>
            <a href="#coach" className="transition-colors duration-200 hover:text-[#F4F4F0]">Coach</a>
            <a href="https://www.instagram.com/toptailoredpropinv" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="flex items-center gap-1.5 transition-colors duration-200 hover:text-[#F4F4F0]">
              <InstagramLogo size={14} /> Instagram
            </a>
            <Link to="/auth" data-testid="footer-login-link" className="transition-colors duration-200 hover:text-[#F4F4F0]">Login</Link>
          </div>
        </div>
        <div className="border-t border-[#2E2E2A] py-6 text-center text-xs text-[#A3A39C]">
          © {new Date().getFullYear()} TTPI Academy. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
